
import { supabase } from "@/integrations/supabase/client";

/**
 * Feedback types allowed in the system
 */
export type FeedbackType = 'Bug' | 'Feature' | 'Improvement' | 'General';

/**
 * Status types for feedback items
 */
export type FeedbackStatus = 'open' | 'in-progress' | 'resolved' | 'rejected';

/**
 * Interface for feedback submission data
 */
export interface FeedbackData {
  userId: string;
  feedbackType: FeedbackType;
  description: string;
  consoleErrors?: string;
  attachmentUrl?: string;
  /** Module slug derived from the route the reporter was on (e.g. "arca"). */
  module?: string | null;
  userMetadata?: Record<string, any>;
}

/**
 * Result of a feedback submission
 */
export interface FeedbackResult {
  success: boolean;
  message: string;
  data?: any;
  error?: any;
}

/**
 * Submits feedback to the Supabase database
 * 
 * @param {FeedbackData} feedbackData - The feedback data to submit
 * @returns {Promise<FeedbackResult>} - Result of the submission operation
 */
export const submitFeedback = async (feedbackData: FeedbackData): Promise<FeedbackResult> => {
  try {
    // Validate required fields
    if (!feedbackData.userId) {
      return {
        success: false,
        message: "User ID is required",
      };
    }

    if (!feedbackData.feedbackType) {
      return {
        success: false,
        message: "Feedback type is required",
      };
    }

    if (!feedbackData.description || feedbackData.description.trim().length < 10) {
      return {
        success: false,
        message: "Description must be at least 10 characters",
      };
    }

    // Prepare the data for insertion
    const insertData = {
      user_id: feedbackData.userId,
      feedback_type: feedbackData.feedbackType,
      description: feedbackData.description.trim(),
      console_errors: feedbackData.consoleErrors?.trim() || null,
      attachment_url: feedbackData.attachmentUrl || null,
      module: feedbackData.module || null,
      user_metadata: feedbackData.userMetadata || {},
      status: 'open' as FeedbackStatus,
    };

    // Insert the feedback into the Supabase table
    const { data, error } = await supabase
      .from('feedback_logs')
      .insert(insertData)
      .select('feedback_id, created_at')
      .single();

    // Handle insertion errors
    if (error) {
      console.error("Feedback submission error:", error);
      
      // Provide more specific error messages based on error codes
      if (error.code === "23505") {
        return {
          success: false,
          message: "This feedback appears to be a duplicate.",
          error,
        };
      } else if (error.code === "23503") {
        return {
          success: false,
          message: "Referenced user does not exist.",
          error,
        };
      } else if (error.code === "42501") {
        return {
          success: false,
          message: "You don't have permission to submit feedback.",
          error,
        };
      }
      
      // Generic error message
      return {
        success: false,
        message: "Failed to submit feedback. Please try again later.",
        error,
      };
    }

    // Return success result with the created data
    return {
      success: true,
      message: "Feedback submitted successfully",
      data,
    };
  } catch (error) {
    // Handle unexpected errors
    console.error("Unexpected error during feedback submission:", error);
    return {
      success: false,
      message: "An unexpected error occurred. Please try again later.",
      error,
    };
  }
};
